import React, { useState, useEffect } from 'react';
import { ref, onValue, runTransaction, update, get, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { jsPDF } from 'jspdf';
import { db, storage } from '../../firebase/config';
import { FaSearch, FaTimes, FaShoppingCart, FaPlus, FaMinus, FaTrash, FaUser } from 'react-icons/fa';
import '../../styles/ProductCard.css';
import panchgiriLogo from '../../img/Panchgiri Logo.jpg';

const AdminPlaceOrder = () => {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [cart, setCart] = useState([]);
    const [showCartModal, setShowCartModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [loadingMessage, setLoadingMessage] = useState('Processing order...');

    // User selection states
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [filteredUsers, setFilteredUsers] = useState([]);

    // Commission toggle state
    const [commissionEnabled, setCommissionEnabled] = useState(true);

    // Notification state
    const [notification, setNotification] = useState({ show: false, message: '', title: '', type: '' });

    const initialFormData = {
        name: '',
        email: '',
        phone: '',
        address: '',
        landmark: '',
        city: '',
        state: '',
        postalCode: '',
        birthDate: '',
        paymentMethod: 'Online'
    };

    const [formData, setFormData] = useState(initialFormData);

    // ============ HELPER FUNCTIONS ============

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return 'N/A';
        if (dateString.includes('/')) {
            const [day, month, year] = dateString.split('/');
            const date = new Date(`${year}-${month}-${day}`);
            const dayNum = String(date.getDate()).padStart(2, '0');
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const monthName = months[date.getMonth()];
            const yearNum = date.getFullYear();
            return `${dayNum} ${monthName} ${yearNum}`;
        }
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const convertDDMMYYYYtoISO = (ddmmyyyy) => {
        if (!ddmmyyyy) return '';
        const parts = ddmmyyyy.split('/');
        if (parts.length !== 3) return ddmmyyyy;
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    };

    const handleBirthDateInput = (e) => {
        let value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length >= 2) {
            value = value.slice(0, 2) + '/' + value.slice(2);
        }
        if (value.length >= 5) {
            value = value.slice(0, 5) + '/' + value.slice(5, 9);
        }
        if (value.length <= 10) {
            const error = validateField('birthDate', value);
            setFormData(prev => ({ ...prev, birthDate: value }));
            setValidationErrors(prev => ({ ...prev, birthDate: error }));
        }
    };

    const generateOrderId = () => {
        const timestamp = Date.now().toString();
        const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        return `ORD-${timestamp}-${random}`;
    };

    const showNotification = (message, title = '', type = 'info') => {
        setNotification({ show: true, message, title, type });
        setTimeout(() => {
            setNotification({ show: false, message: '', title: '', type: '' });
        }, 5000);
    };

    // ============ VALIDATION ============

    const validateField = (fieldName, value) => {
        switch (fieldName) {
            case 'name':
                if (!value || value.trim() === '') return 'Name is required';
                if (value.length < 3) return 'Name must be at least 3 characters';
                if (!/^[a-zA-Z\s]+$/.test(value)) return 'Name should contain only letters';
                return '';

            case 'email':
                if (!value || value.trim() === '') return 'Email is required';
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
                return '';

            case 'phone':
                if (!value || value.trim() === '') return 'Phone number is required';
                if (!/^[6-9]\d{9}$/.test(value)) return 'Invalid phone number (10 digits, starting with 6-9)';
                return '';

            case 'address':
                if (!value || value.trim() === '') return 'Address is required';
                if (value.length < 5) return 'Address must be at least 5 characters';
                return '';

            case 'city':
                if (!value || value.trim() === '') return 'City is required';
                if (!/^[a-zA-Z\s]+$/.test(value)) return 'City should contain only letters';
                return '';

            case 'state':
                if (!value || value.trim() === '') return 'State is required';
                return '';

            case 'postalCode':
                if (!value || value.trim() === '') return 'Postal code is required';
                if (!/^\d{6}$/.test(value)) return 'Invalid postal code (6 digits required)';
                return '';

            case 'birthDate':
                if (!value || value.trim() === '') return 'Birth date is required';
                const parts = value.split('/');
                if (parts.length !== 3) return 'Invalid date format (DD/MM/YYYY)';

                const [day, month, year] = parts.map(p => parseInt(p, 10));
                if (isNaN(day) || isNaN(month) || isNaN(year)) return 'Invalid date';
                if (day < 1 || day > 31) return 'Day must be between 1-31';
                if (month < 1 || month > 12) return 'Month must be between 1-12';
                if (year < 1900 || year > new Date().getFullYear()) return 'Invalid year';

                const date = new Date(year, month - 1, day);
                if (date.getDate() !== day || date.getMonth() !== month - 1) {
                    return 'Invalid date';
                }

                const age = new Date().getFullYear() - year;
                if (age < 18) return 'Must be at least 18 years old';
                if (age > 120) return 'Invalid age';

                return '';

            default:
                return '';
        }
    };

    const validateAllFields = () => {
        const errors = {};
        Object.keys(formData).forEach(field => {
            if (field !== 'landmark' && field !== 'paymentMethod') {
                const error = validateField(field, formData[field]);
                if (error) errors[field] = error;
            }
        });
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // ============ FETCH DATA ============

    useEffect(() => {
        // Fetch products
        const productsRef = ref(db, 'HTAMS/company/products');
        const unsubscribeProducts = onValue(productsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const productsArray = Object.entries(data).map(([id, product]) => ({
                    id,
                    ...product,
                }));
                setProducts(productsArray);
                setFilteredProducts(productsArray);
            }
        });

        // Fetch all users
        const usersRef = ref(db, 'HTAMS/users');
        const unsubscribeUsers = onValue(usersRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const usersArray = Object.entries(data).map(([uid, userData]) => ({
                    uid,
                    ...userData,
                }));
                setUsers(usersArray);
                setFilteredUsers(usersArray);
            }
        });

        return () => {
            unsubscribeProducts();
            unsubscribeUsers();
        };
    }, []);

    // ============ SEARCH & FILTER ============

    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredProducts(products);
        } else {
            const filtered = products.filter(product =>
                product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                product.category?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredProducts(filtered);
        }
    }, [searchQuery, products]);

    useEffect(() => {
        if (userSearchQuery.trim() === '') {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(user =>
                user.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                user.email?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                user.phone?.includes(userSearchQuery) ||
                user.currentLevel?.toLowerCase().includes(userSearchQuery.toLowerCase())
            );
            setFilteredUsers(filtered);
        }
    }, [userSearchQuery, users]);

    // ============ CART FUNCTIONS ============

    const addToCart = (product) => {
        const existingItem = cart.find(item => item.id === product.id);
        if (existingItem) {
            const newQuantity = existingItem.quantity + 1;
            const maxStock = Number(product?.stock) || 0;

            if (newQuantity > maxStock) {
                showNotification(
                    `Only ${maxStock} units available in stock`,
                    '⚠️ Stock Limit Reached',
                    'warning'
                );
                return;
            }

            updateQuantity(product.id, newQuantity);
            const price = product.price || product.mrp || 0;
            showNotification(
                `Quantity updated to ${newQuantity} (₹${(price * newQuantity).toFixed(2)})`,
                `🛒 ${product.name}`,
                'success'
            );
        } else {
            setCart([...cart, { ...product, quantity: 1 }]);
            const price = product.price || product.mrp || 0;
            showNotification(
                `Added to cart (Qty: 1, Price: ₹${price})`,
                `✨ ${product.name}`,
                'success'
            );
        }
    };

    const updateQuantity = (productId, newQuantity) => {
        const product = products.find(p => p.id === productId);
        const maxStock = Number(product?.stock) || 0;

        if (newQuantity > maxStock) {
            alert(`Only ${maxStock} units available in stock`);
            return;
        }

        if (newQuantity <= 0) {
            removeFromCart(productId);
        } else {
            setCart(cart.map(item =>
                item.id === productId ? { ...item, quantity: newQuantity } : item
            ));
        }
    };

    const removeFromCart = (productId) => {
        setCart(cart.filter(item => item.id !== productId));
    };

    const clearCart = () => {
        setCart([]);
    };

    const getCartTotal = () => {
        return cart.reduce((total, item) => {
            const price = item.price || item.mrp || 0;
            return total + (price * item.quantity);
        }, 0);
    };

    const getCartItemCount = () => {
        return cart.reduce((count, item) => count + item.quantity, 0);
    };

    // ============ ORDER PROCESSING ============

    const completeOrder = async (orderData, orderId) => {
        try {
            console.log('=== COMPLETING ORDER ===');
            console.log('Order ID:', orderId);

            // Check if order exists to prevent duplicate processing
            const orderRef = ref(db, `HTAMS/orders/${orderId}`);
            const orderSnap = await get(orderRef);
            if (orderSnap.exists()) {
                console.log('⚠️ Order already processed. Skipping duplicate request.');
                resetForm();
                setLoading(false);
                return;
            }

            // STEP 1: Deduct stock
            console.log('=== DEDUCTING STOCK ===');
            for (const item of cart) {
                const productStockRef = ref(db, `HTAMS/company/products/${item.id}/stock`);
                const transactionResult = await runTransaction(productStockRef, (currentStock) => {
                    const stockNum = Number(currentStock) || 0;
                    if (stockNum < item.quantity) {
                        console.error(`Insufficient stock for ${item.name}: ${stockNum} < ${item.quantity}`);
                        return;
                    }
                    return stockNum - item.quantity;
                });

                if (!transactionResult.committed) {
                    throw new Error(`Failed to deduct stock for ${item.name}. Stock may have been sold out.`);
                }
                console.log(`✅ Stock deducted for ${item.name}: ${item.quantity} units`);
            }
            console.log('✅ All stock deducted successfully');

            // STEP 2: Save order
            console.log('=== SAVING ORDER TO FIREBASE ===');
            const cleanOrderData = JSON.parse(JSON.stringify(orderData, (key, value) => {
                return value === undefined ? null : value;
            }));

            const orderDataWithId = {
                ...cleanOrderData,
                orderId: orderId,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                placedByAdmin: true,
                attributedToUser: selectedUser?.uid
            };

            await set(ref(db, `HTAMS/orders/${orderId}`), orderDataWithId);
            console.log('✅ Order saved to Firebase');

            // STEP 3: Process commission for selected user (only if commission is enabled)
            let commissionResults = [];
            if (commissionEnabled) {
                console.log('🎯 Commission is ENABLED - Processing commission...');
                for (const item of cart) {
                    try {
                        const itemPrice = item.price || item.mrp || 0;
                        const itemOrderData = {
                            totalAmount: itemPrice * item.quantity,
                            productId: item.id,
                            productName: item.name,
                            quantity: item.quantity,
                            customerName: formData.name,
                            customerPhone: formData.phone,
                            customerEmail: formData.email,
                        };

                        console.log(`Processing commission for ${item.name}: ₹${itemOrderData.totalAmount}`);
                        const commissionResult = await processCommission(itemOrderData, `${orderId}_${item.id}`);
                        commissionResults.push({ productId: item.id, result: commissionResult });

                        await saveSaleDetails(itemOrderData, `${orderId}_${item.id}`, commissionResult);
                        console.log(`✅ Commission processed for ${item.name}`);
                    } catch (error) {
                        console.error(`❌ Commission processing failed for ${item.name}:`, error);
                        commissionResults.push({ productId: item.id, result: { ok: false, error: error.message } });
                        // Don't throw - continue with order even if commission fails
                        // The sale will be marked as commissionDistributed: false
                    }
                }
            } else {
                console.log('⚠️ Commission is DISABLED - Skipping commission processing...');
                // Save sale details without commission for each item
                for (const item of cart) {
                    const itemPrice = item.price || item.mrp || 0;
                    const itemOrderData = {
                        totalAmount: itemPrice * item.quantity,
                        productId: item.id,
                        productName: item.name,
                        quantity: item.quantity,
                        customerName: formData.name,
                        customerPhone: formData.phone,
                        customerEmail: formData.email,
                    };
                    // Pass null as commissionResult to indicate no commission processing
                    await saveSaleDetails(itemOrderData, `${orderId}_${item.id}`, null);
                    commissionResults.push({ productId: item.id, result: { ok: false, error: 'Commission disabled by admin' } });
                }
            }

            // STEP 4: Generate invoice
            await generateMultiProductPdfBill({
                ...orderData,
                orderId: orderId,
                items: cart,
                saleDate: new Date().toLocaleString(),
            });

            const successfulCommissions = commissionResults.filter(r => r.result.ok).length;

            if (commissionEnabled) {
                console.log(`Order placed successfully! Commission distributed for ${successfulCommissions} out of ${cart.length} products.`);
                showNotification(
                    `Order completed! Commission credited to ${selectedUser?.name}`,
                    '✅ Order Successful',
                    'success'
                );
            } else {
                console.log('Order placed successfully! Commission distribution was disabled.');
                showNotification(
                    'Order completed! Commission distribution was disabled for this order.',
                    '✅ Order Successful (No Commission)',
                    'success'
                );
            }

            resetForm();
        } catch (error) {
            console.error('❌ Error in completeOrder:', error);
            throw error;
        }
    };

    const processCommission = async (orderData, orderId) => {
        console.log('=== STARTING COMMISSION PROCESSING ===');

        if (!selectedUser?.uid) {
            console.warn('⚠️ No user selected for commission distribution');
            return { ok: false, error: 'No user selected' };
        }

        try {
            const apiPayload = {
                sellerId: selectedUser.uid,
                amount: orderData.totalAmount,
                product: {
                    id: orderData.productId,
                    name: orderData.productName,
                },
                orderId: orderId,
                idempotencyKey: `order_${orderId}`,
            };

            console.log('=== API PAYLOAD ===', apiPayload);

            const response = await fetch('https://processsale-ph2yhuhnga-uc.a.run.app/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(apiPayload),
            });

            console.log('Response status:', response.status);
            const responseText = await response.text();
            console.log('Raw response:', responseText);

            if (!response.ok) {
                throw new Error(`API call failed: ${response.status} - ${responseText}`);
            }

            const result = JSON.parse(responseText);
            console.log('Parsed result:', result);
            return result;

        } catch (error) {
            console.error('Commission API call failed:', error);
            // Return a failure result instead of throwing
            return { ok: false, error: error.message };
        }
    };

    const saveSaleDetails = async (orderData, orderId, commissionResult) => {
        try {
            let saleData;

            // Check if commission was processed and successful
            if (commissionResult?.ok && commissionResult?.paidTo) {
                const commissionsObject = {};
                commissionResult.paidTo.forEach(recipient => {
                    commissionsObject[recipient.uid] = {
                        amount: recipient.amount,
                        diff: recipient.diff,
                        rate: recipient.rate,
                        role: recipient.role,
                    };
                });

                const sellerInfo = commissionResult.paidTo.find(p => p.uid === selectedUser?.uid);
                const roleAtSale = sellerInfo?.role || selectedUser?.currentLevel || null;

                saleData = {
                    sellerId: selectedUser?.uid || 'unknown',
                    amount: orderData.totalAmount,
                    product: {
                        id: orderData.productId,
                        name: orderData.productName,
                    },
                    orderId: orderId,
                    roleAtSale: roleAtSale,
                    timestamp: Date.now(),
                    commissions: commissionsObject,
                    customerName: orderData.customerName,
                    customerPhone: orderData.customerPhone,
                    customerEmail: orderData.customerEmail,
                    quantity: orderData.quantity,
                    unitPrice: orderData.totalAmount / orderData.quantity,
                    paymentMethod: orderData.paymentMethod || 'Online Payment (Razorpay)',
                    saleDate: new Date().toISOString(),
                    status: 'Confirmed',
                    commissionDistributed: true,
                    placedByAdmin: true,
                };
            } else {
                // Commission was either disabled or failed
                const isCommissionDisabled = commissionResult === null;
                saleData = {
                    sellerId: selectedUser?.uid || 'unknown',
                    amount: orderData.totalAmount,
                    product: {
                        id: orderData.productId,
                        name: orderData.productName,
                    },
                    orderId: orderId,
                    roleAtSale: selectedUser?.currentLevel || null,
                    timestamp: Date.now(),
                    commissions: {},
                    customerName: orderData.customerName,
                    customerPhone: orderData.customerPhone,
                    customerEmail: orderData.customerEmail,
                    quantity: orderData.quantity,
                    unitPrice: orderData.totalAmount / orderData.quantity,
                    paymentMethod: orderData.paymentMethod || 'Online Payment (Razorpay)',
                    saleDate: new Date().toISOString(),
                    status: 'Confirmed',
                    commissionDistributed: false,
                    commissionError: isCommissionDisabled ? 'Commission disabled by admin' : (commissionResult?.error || 'Commission processing failed'),
                    commissionDisabledByAdmin: isCommissionDisabled,
                    placedByAdmin: true,
                };
            }

            console.log('Saving sale details:', saleData);
            const saleRef = ref(db, `HTAMS/salesDetails/${orderId}`);
            await set(saleRef, saleData);

            console.log('Sale details saved successfully');
            return saleData;
        } catch (error) {
            console.error('Error saving sale details:', error);
            throw error;
        }
    };

    const generateMultiProductPdfBill = async (billData) => {
        try {
            console.log('=== GENERATING PDF INVOICE ===');
            console.log('Bill Data:', {
                orderId: billData.orderId,
                customerName: billData.customerName,
                itemCount: billData.items?.length,
                totalAmount: billData.totalAmount
            });

            const doc = new jsPDF();
            const address = billData.customerAddress;
            const fullAddress = `${address.street}${address.landmark ? `, ${address.landmark}` : ''}, ${address.city}, ${address.state} - ${address.postalCode}`;

            // Page dimensions
            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 20;

            // Generate invoice details
            const timestamp = Date.now().toString();
            const invoiceNo = `${timestamp.slice(0, 5)}-${timestamp.slice(5, 7)}`;
            const invoiceDate = new Date();
            const formattedDate = invoiceDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
            const formattedTime = invoiceDate.toLocaleTimeString('en-US', { weekday: 'long', hour: '2-digit', minute: '2-digit' });

            // CORRECT GST CALCULATION - Invoice Price is GST-INCLUSIVE
            // If Invoice Price = 100, and GST = 5%, then:
            // Amount (base) = 100 / 1.05 = 95.24
            // GST = 100 - 95.24 = 4.76
            // Total = 95.24 + 4.76 = 100
            const GST_RATE = 0.05; // 5% GST

            // Calculate total invoice price (GST-inclusive)
            const totalInvoicePrice = billData.items.reduce((total, item) => {
                const itemPrice = item.invoicePrice || item.price || item.mrp || 0;
                return total + (itemPrice * item.quantity);
            }, 0);

            // Calculate base amount (excluding GST) from invoice price
            // Formula: Base = Invoice Price / (1 + GST Rate)
            const baseAmount = totalInvoicePrice / (1 + GST_RATE);

            // Calculate GST amount
            const gstAmount = totalInvoicePrice - baseAmount;

            // Total amount is the invoice price (GST-inclusive)
            const totalAmount = totalInvoicePrice;

            const isIntraState = address.state === 'Maharashtra';
            const sgstAmount = isIntraState ? gstAmount / 2 : 0;
            const cgstAmount = isIntraState ? gstAmount / 2 : 0;
            const igstAmount = isIntraState ? 0 : gstAmount;

            console.log('PDF Amount Calculations:', {
                totalInvoicePrice: totalInvoicePrice,
                baseAmount: baseAmount,
                gstAmount: gstAmount,
                sgstAmount: sgstAmount,
                cgstAmount: cgstAmount,
                igstAmount: igstAmount,
                totalAmount: totalAmount,
                isIntraState: isIntraState
            });

            // Background
            doc.setFillColor(240, 244, 232);
            doc.rect(0, 0, pageWidth, pageHeight, 'F');

            // Logo
            try {
                doc.addImage(panchgiriLogo, 'jpg', (pageWidth - 30) / 2, 15, 30, 30);
            } catch (e) { console.warn('Logo error'); }

            // Title
            doc.setFontSize(36);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(26, 26, 26);
            doc.text('INVOICE', pageWidth / 2, 55, { align: 'center' });

            // Invoice details
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(74, 85, 104);
            doc.text(`No. ${invoiceNo}`, margin, 68);
            doc.text(`GSTIN: 27AISPB8262F2ZO`, margin, 74);
            doc.text(formattedDate, pageWidth - margin, 68, { align: 'right' });
            doc.text(formattedTime, pageWidth - margin, 74, { align: 'right' });

            // Customer
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(45, 55, 72);
            doc.text('INVOICE TO:', margin, 86);

            doc.setFontSize(13);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(26, 26, 26);
            doc.text(billData.customerName, pageWidth - margin, 86, { align: 'right' });

            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(74, 85, 104);
            doc.text(`Mobile: ${billData.customerPhone}`, pageWidth - margin, 93, { align: 'right' });
            doc.text(`Address: ${fullAddress}`, pageWidth - margin, 99, { align: 'right', maxWidth: 100 });

            // TABLE WITH SR NO.
            const tableStartY = 110;
            const tableHeaderHeight = 10;

            doc.setFillColor(255, 255, 255);
            doc.roundedRect(margin, tableStartY, pageWidth - (2 * margin), tableHeaderHeight, 2, 2, 'F');

            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(45, 55, 72);
            doc.text('SR', margin + 8, tableStartY + 8);
            doc.text('DESCRIPTION', margin + 22, tableStartY + 8);
            doc.text('PRICE', pageWidth - 110, tableStartY + 8, { align: 'center' });
            doc.text('QTY', pageWidth - 65, tableStartY + 8, { align: 'center' });
            doc.text('TOTAL', pageWidth - margin - 10, tableStartY + 8, { align: 'right' });

            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(1);
            doc.line(margin + 5, tableStartY + tableHeaderHeight, pageWidth - margin - 5, tableStartY + tableHeaderHeight);

            // Table rows
            let currentY = tableStartY + tableHeaderHeight + 6;
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(74, 85, 104);

            billData.items.forEach((item, index) => {
                if (currentY > pageHeight - 70) {
                    doc.addPage();
                    doc.setFillColor(240, 244, 232);
                    doc.rect(0, 0, pageWidth, pageHeight, 'F');
                    currentY = 30;
                }

                // Line total = invoice price (GST-inclusive) × quantity
                const itemPrice = item.invoicePrice || item.price || item.mrp || 0;
                const lineTotal = itemPrice * item.quantity;

                console.log(`PDF Item ${index + 1}:`, {
                    name: item.name,
                    itemPrice: itemPrice,
                    quantity: item.quantity,
                    lineTotal: lineTotal,
                    rawItem: { invoicePrice: item.invoicePrice, price: item.price, mrp: item.mrp }
                });

                // Sr No.
                doc.text((index + 1).toString(), margin + 10, currentY);

                // Description
                doc.text(item.name, margin + 22, currentY, { maxWidth: 75 });

                // Price (invoice price - GST-inclusive)
                doc.text('Rs.' + String(itemPrice.toFixed(2)), pageWidth - 110, currentY, { align: 'center' });

                // Qty
                doc.setFont('helvetica', 'bold');
                doc.text(item.quantity.toString(), pageWidth - 65, currentY, { align: 'center' });

                // TOTAL = invoice price × quantity (GST-inclusive)
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(26, 26, 26);
                doc.text('Rs.' + String(lineTotal.toFixed(2)), pageWidth - margin - 10, currentY, { align: 'right' });

                doc.setFont('helvetica', 'normal');
                doc.setTextColor(74, 85, 104);

                currentY += 10;

                if (index < billData.items.length - 1) {
                    doc.setDrawColor(230, 235, 240);
                    doc.setLineWidth(0.3);
                    doc.line(margin + 10, currentY - 6, pageWidth - margin - 10, currentY - 6);
                }
            });

            // TOTALS — MATCHING YOUR SCREENSHOT
            const totalsY = currentY + 6;
            doc.setDrawColor(203, 213, 224);
            doc.setLineWidth(1.5);
            doc.line(margin + 5, totalsY, pageWidth - margin - 5, totalsY);

            // Amount = (base total only)
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(45, 55, 72);
            doc.text('Subtotal', pageWidth - margin - 75, totalsY + 8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(26, 26, 26);
            doc.text('Rs.' + String(baseAmount.toFixed(2)), pageWidth - margin - 5, totalsY + 8, { align: 'right' });

            // GST
            let currentLineY = totalsY + 15;

            if (isIntraState) {
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(45, 55, 72);
                doc.text('SGST (2.5%)', pageWidth - margin - 75, currentLineY);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(26, 26, 26);
                doc.text('Rs.' + String(sgstAmount.toFixed(2)), pageWidth - margin - 5, currentLineY, { align: 'right' });

                currentLineY += 7;
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(45, 55, 72);
                doc.text('CGST (2.5%)', pageWidth - margin - 75, currentLineY);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(26, 26, 26);
                doc.text('Rs.' + String(cgstAmount.toFixed(2)), pageWidth - margin - 5, currentLineY, { align: 'right' });
            } else {
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(45, 55, 72);
                doc.text('IGST (5%)', pageWidth - margin - 75, currentLineY);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(26, 26, 26);
                doc.text('Rs.' + String(igstAmount.toFixed(2)), pageWidth - margin - 5, currentLineY, { align: 'right' });
            }

            // Final Total
            const totalLineY = currentLineY + 4;
            doc.setDrawColor(203, 213, 224);
            doc.setLineWidth(1);
            doc.line(pageWidth - margin - 80, totalLineY, pageWidth - margin, totalLineY);

            doc.setFontSize(13);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(45, 55, 72);
            doc.text('Total Amount', pageWidth - margin - 75, totalLineY + 9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(26, 26, 26);
            doc.text('Rs.' + String(totalAmount.toFixed(2)), pageWidth - margin - 5, totalLineY + 9, { align: 'right' });

            // Amount in words
            const amountInWords = convertAmountToWords(Math.floor(totalAmount));
            doc.setFontSize(8);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(74, 85, 104);
            doc.text(amountInWords, pageWidth - margin - 5, totalLineY + 23, { align: 'right', maxWidth: 100 });

            // Footer (unchanged)
            const footerY = totalLineY + 23;
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(45, 55, 72);
            doc.text('SEND PAYMENTS TO:', margin + 5, footerY);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(74, 85, 104);
            doc.text(billData.customerName, margin + 5, footerY + 8);
            doc.text(billData.customerEmail, margin + 5, footerY + 15);

            doc.setFontSize(30);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(74, 124, 89);
            doc.text('Thank You!', pageWidth - margin - 5, footerY + 12, { align: 'right' });

            doc.setFontSize(8);
            doc.setTextColor(120, 120, 120);
            doc.text(`Payment: ${billData.paymentMethod} | Items: ${billData.totalItems}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

            // Save & Upload (unchanged)
            const fileName = `Invoice_${invoiceNo}_${billData.customerName.replace(/\s+/g, '_')}.pdf`;
            const pdfBlob = doc.output('blob');
            doc.save(fileName);

            showNotification(`Invoice downloaded successfully`, `File: ${fileName}`, 'success');

            // Firebase upload
            try {
                console.log('=== UPLOADING PDF TO FIREBASE STORAGE ===');
                const orderId = billData.orderId || generateOrderId();
                const storagePath = `orders/${orderId}/invoice/${fileName}`;
                console.log('Storage path:', storagePath);

                const pdfStorageRef = storageRef(storage, storagePath);

                console.log('Uploading PDF to Firebase Storage...');
                await uploadBytes(pdfStorageRef, pdfBlob, { contentType: 'application/pdf' });
                console.log('✅ PDF uploaded successfully');

                const downloadURL = await getDownloadURL(pdfStorageRef);
                console.log('Download URL:', downloadURL);

                console.log('Saving invoice metadata to database...');
                await set(ref(db, `HTAMS/orders/${orderId}/invoice`), {
                    invoiceNumber: invoiceNo,
                    totalAmount,
                    baseAmount,
                    gstAmount,
                    downloadURL,
                    fileName,
                    generatedAt: new Date().toISOString(),
                    uploadedBy: 'admin',
                    status: 'generated'
                });
                console.log('✅ Invoice metadata saved to database');

                showNotification(`Invoice uploaded to Firebase`, fileName, 'success');
            } catch (err) {
                console.error('❌ Firebase upload error:', err);
                console.error('Error details:', err.message);
                showNotification(`Upload failed: ${err.message}`, 'PDF saved locally but not uploaded', 'error');
            }


        } catch (error) {
            console.error('❌ PDF Generation Error:', error);
            console.error('Error details:', error.message);
            console.error('Stack trace:', error.stack);
            alert('Failed to generate invoice: ' + error.message);
        }
    };

    const convertAmountToWords = (amount) => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        if (amount === 0) return 'Zero Rupees Only';

        let words = '';
        let crores = Math.floor(amount / 10000000);
        let lakhs = Math.floor((amount % 10000000) / 100000);
        let thousands_part = Math.floor((amount % 100000) / 1000);
        let hundreds = Math.floor((amount % 1000) / 100);
        let remainder = amount % 100;

        if (crores > 0) {
            words += convertNumberToWords(crores) + ' Crore ';
        }
        if (lakhs > 0) {
            words += convertNumberToWords(lakhs) + ' Lakh ';
        }
        if (thousands_part > 0) {
            words += convertNumberToWords(thousands_part) + ' Thousand ';
        }
        if (hundreds > 0) {
            words += ones[hundreds] + ' Hundred ';
        }
        if (remainder > 0) {
            if (remainder < 10) {
                words += ones[remainder];
            } else if (remainder < 20) {
                words += teens[remainder - 10];
            } else {
                words += tens[Math.floor(remainder / 10)];
                if (remainder % 10 > 0) {
                    words += ' ' + ones[remainder % 10];
                }
            }
        }

        return words.trim() + ' Rupees Only';
    };


    const convertNumberToWords = (num) => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        if (num < 10) return ones[num];
        if (num < 20) return teens[num - 10];
        if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 > 0 ? ' ' + ones[num % 10] : '');
        return '';
    };

    const submitOrder = async () => {
        if (!selectedUser) {
            alert('⚠️ Please select a user to attribute this order to');
            return;
        }

        if (cart.length === 0) {
            alert('Please add items to cart before placing order');
            return;
        }

        if (!validateAllFields()) {
            alert('Please fix all validation errors before submitting');
            return;
        }

        setLoading(true);
        setLoadingMessage('Processing order...');
        console.log('=== STARTING ADMIN ORDER PLACEMENT ===');
        console.log('Selected User:', selectedUser);

        try {
            // Check stock availability
            console.log('=== CHECKING STOCK AVAILABILITY ===');
            for (const item of cart) {
                const productStockRef = ref(db, `HTAMS/company/products/${item.id}/stock`);
                const stockSnapshot = await get(productStockRef);
                const currentStock = Number(stockSnapshot.val()) || 0;

                console.log(`${item.name}: Available=${currentStock}, Required=${item.quantity}`);

                if (currentStock < item.quantity) {
                    alert(`❌ Insufficient stock for ${item.name}\n\nAvailable: ${currentStock}\nRequired: ${item.quantity}`);
                    setLoading(false);
                    return;
                }
            }
            console.log('✅ All items have sufficient stock');

            const totalAmount = getCartTotal();
            const orderData = {
                items: cart.map(item => {
                    const itemPrice = item.price || item.mrp || 0;
                    return {
                        productId: item.id,
                        productName: item.name,
                        quantity: item.quantity,
                        unitPrice: itemPrice,
                        totalPrice: itemPrice * item.quantity,
                        basePrice: item.basePrice || itemPrice,
                    };
                }),
                totalAmount: totalAmount,
                totalItems: getCartItemCount(),
                orderDate: new Date().toISOString(),
                placedBy: selectedUser.uid,  // Set to selected user's UID
                attributedToUser: selectedUser.uid,  // Keep for reference
                placedByAdmin: true,  // Flag to indicate admin placed the order
                customerName: formData.name,
                customerEmail: formData.email,
                customerPhone: formData.phone,
                customerAddress: {
                    street: formData.address,
                    landmark: formData.landmark,
                    city: formData.city,
                    state: formData.state,
                    postalCode: formData.postalCode,
                },
                birthDate: formData.birthDate,
                paymentMethod: formData.paymentMethod,
                status: 'Confirmed',  // Set to Confirmed as per requirement
                paymentDetails: {
                    payment_method: formData.paymentMethod,
                    payment_status: 'admin_placed',
                    payment_timestamp: new Date().toISOString(),
                },
            };

            // Generate sequential order ID
            const generateSequentialOrderId = async () => {
                const counterRef = ref(db, 'HTAMS/meta/orderIdCounter');
                const result = await runTransaction(counterRef, (current) => {
                    return (current || 0) + 1;
                });

                const count = result.snapshot.val();
                // Format: order-01, order-02, ... order-10, ...
                return `order-${String(count).padStart(2, '0')}`;
            };

            const orderId = await generateSequentialOrderId();
            await completeOrder(orderData, orderId);

            if (commissionEnabled) {
                alert(`✅ Order placed successfully!\n\nOrder ID: ${orderId}\nCommission credited to: ${selectedUser.name}\nInvoice downloaded.`);
            } else {
                alert(`✅ Order placed successfully!\n\nOrder ID: ${orderId}\nCommission was NOT distributed (disabled)\nInvoice downloaded.`);
            }
            setLoading(false);

        } catch (err) {
            console.error('=== ORDER PROCESSING ERROR ===', err);
            alert('Failed to place order. An error occurred. Check console for details.');
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowCartModal(false);
        setFormData(initialFormData);
        setValidationErrors({});
        clearCart();
    };

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        setUserSearchQuery('');
        setShowUserDropdown(false);
    };

    // ============ RENDER UI ============

    return (
        <div style={{ padding: '10px', maxWidth: '100%', margin: '0 auto' }}>
            <style>{`
        .admin-place-order-container {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        .top-header-bar {
        
          top: 0;
          left: 0;
          right: 0;
          background: var(--mobile-bg-primary);
          color: white;
          padding: 15px 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          z-index: 1000;
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: var(--mobile-text-primary);
        }
        
        .header-title {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 0;
          color: white;
        }
        
        .cart-icon-btn {
          position: relative;
          background: linear-gradient(to right, var(--mobile-bg-primary), var(--mobile-bg-secondary));
          border: 2px solid white;
          color: white;
          padding: 10px 20px;
          border-radius: 25px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 600;
          transition: all 0.3s;
        }
        
        .cart-icon-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.05);
        }
        
        .cart-count-badge {
          background: #f44336;
          color: white;
          border-radius: 50%;
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: bold;
        }
        
        .user-selection-section {
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin-bottom: 30px;
        }
        
        .user-search-container {
          position: relative;
          margin-top: 15px;
        }
        
        .user-search-input {
          width: 100%;
          padding: 12px 45px 12px 15px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 16px;
          transition: all 0.3s;
        }
        
        .user-search-input:focus {
          outline: none;
          border-color: #4caf50;
        }
        
        .user-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 300px;
          overflow-y: auto;
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          margin-top: 5px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          z-index: 1000;
        }
        
        .user-dropdown-item {
          padding: 12px 15px;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
          transition: background 0.2s;
        }
        
        .user-dropdown-item:hover {
          background: #f5f5f5;
        }
        
        .user-dropdown-item:last-child {
          border-bottom: none;
        }
        
        .selected-user-badge {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          padding: 16px 24px;
          border-radius: 12px;
          margin-top: 15px;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          display: flex;
          align-items: center;
          gap: 15px;
        }
        
        .selected-user-icon {
          background: rgba(255, 255, 255, 0.2);
          padding: 12px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .selected-user-info {
          flex: 1;
        }
        
        .selected-user-name {
          font-size: 1.2rem;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .selected-user-details {
          font-size: 0.9rem;
          opacity: 0.95;
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }
        
        .selected-user-detail-item {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        
        .remove-user-btn {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }
        
        .remove-user-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: rotate(90deg);
        }
        
        .commission-toggle-section {
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin-bottom: 30px;
          border: 2px solid #e0e0e0;
          transition: all 0.3s;
        }
        
        .commission-toggle-section:hover {
          border-color: #4caf50;
        }
        
        .commission-toggle-control {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        
        .toggle-label {
          display: flex;
          align-items: center;
          gap: 15px;
          width: 100%;
          cursor: pointer;
        }
        
        .toggle-label-text {
          font-size: 1rem;
          font-weight: 600;
          color: #333;
          flex: 1;
        }
        
        .toggle-switch-wrapper {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .toggle-checkbox {
          display: none;
        }
        
        .toggle-switch {
          position: relative;
          width: 120px;
          height: 48px;
          background: linear-gradient(135deg, #f44336 0%, #e53935 100%);
          border-radius: 24px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          display: flex;
          align-items: center;
          padding: 4px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        
        .toggle-checkbox:checked + .toggle-switch {
          background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
        }
        
        .toggle-slider {
          position: absolute;
          width: 40px;
          height: 40px;
          background: white;
          border-radius: 50%;
          transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          left: 4px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        
        .toggle-checkbox:checked + .toggle-switch .toggle-slider {
          left: 76px;
        }
        
        .toggle-status {
          position: absolute;
          font-weight: bold;
          font-size: 0.85rem;
          color: white;
          transition: all 0.3s;
        }
        
        .toggle-status.on {
          left: 15px;
        }
        
        .toggle-status.off {
          right: 15px;
        }
        
        .commission-warning {
          background: linear-gradient(135deg, #fff3cd 0%, #fff8e1 100%);
          border-left: 4px solid #ff9800;
          color: #856404;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          margin-top: 12px;
          box-shadow: 0 2px 6px rgba(255, 152, 0, 0.15);
          animation: fadeIn 0.3s;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .product-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }
        
        .cart-floating-btn {
          position: fixed;
          bottom: 30px;
          right: 30px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 50%;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          z-index: 999;
          transition: transform 0.3s;
        }
        
        .cart-floating-btn:hover {
          transform: scale(1.1);
        }
        
        .cart-badge {
          position: absolute;
          top: -5px;
          right: -5px;
          background: #f44336;
          color: white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
        }
        
        .notification {
          position: fixed;
          top: 80px;
          right: 20px;
          background: white;
          padding: 16px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          max-width: 350px;
          z-index: 10000;
          animation: slideIn 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(450px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .notification.success {
          border-left: 5px solid #4caf50;
          background: linear-gradient(135deg, #ffffff 0%, #f1f8f4 100%);
        }
        
        .notification.error {
          border-left: 5px solid #f44336;
          background: linear-gradient(135deg, #ffffff 0%, #fef1f0 100%);
        }
        
        .notification.warning {
          border-left: 5px solid #ff9800;
          background: linear-gradient(135deg, #ffffff 0%, #fff8f0 100%);
        }
        
        .notification.info {
          border-left: 5px solid #2196f3;
          background: linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%);
        }
        
        .notification-title {
          font-weight: bold;
          font-size: 1.1rem;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .notification-message {
          font-size: 0.95rem;
          color: #555;
          line-height: 1.4;
        }
        
        .loading-overlay-full {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(5px);
          z-index: 99999;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }
        
        .loading-bar-wrapper {
          width: 90%;
          max-width: 400px;
          text-align: center;
        }
        
        .loading-bar-container {
          width: 100%;
          padding: 20px;
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          border-bottom: 2px solid #10b981;
          text-align: center;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
          border-radius: 12px;
        }
        
        .loading-bar {
          width: 100%;
          max-width: 500px;
          height: 8px;
          background: #d1fae5;
          border-radius: 4px;
          overflow: hidden;
          margin: 0 auto 12px;
          position: relative;
          box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .loading-bar::after {
          content: '';
          position: absolute;
          width: 40%;
          height: 100%;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          animation: loadingSlide 1.5s ease-in-out infinite;
          box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
        }
        
        @keyframes loadingSlide {
          0% {
            left: -40%;
          }
          100% {
            left: 100%;
          }
        }
        
        .loading-text {
          color: #047857;
          font-weight: 600;
          font-size: 1rem;
          margin: 0;
        }
      `}</style>

            {/* Fixed Top Header with Cart */}
            {/* <div className="top-header-bar">
                <h1 className="header-title">Admin Place Order</h1>
                {cart.length > 0 && (
                    <button className="cart-icon-btn" onClick={() => setShowCartModal(true)}>
                        <FaShoppingCart size={20} />
                        <span>Cart</span>
                        <span className="cart-count-badge">{getCartItemCount()}</span>
                    </button>
                )}
            </div> */}

            <div className="admin-place-order-container">
                <div className="top-header-bar">

                    <p style={{ color: 'var(--mobile-text-primary)', marginBottom: '30px', fontSize: '1rem' }}>
                        Place orders on behalf of users. Commission will be credited to the selected user.
                    </p>
                    {cart.length > 0 && (
                        <button className="cart-icon-btn" onClick={() => setShowCartModal(true)}>
                            <FaShoppingCart size={20} />
                            <span>Cart</span>
                            <span className="cart-count-badge">{getCartItemCount()}</span>
                        </button>
                    )}

                </div>

                {/* User Selection Section */}
                <div className="user-selection-section">
                    <h2 style={{ fontSize: '1.2rem', marginBottom: '10px', color: '#333' }}>
                        <FaUser style={{ marginRight: '10px' }} />
                        Select User
                    </h2>
                    <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '15px' }}>
                        Search and select the user to whom this order's commission should be credited
                    </p>

                    {selectedUser ? (
                        <div>
                            <div className="selected-user-badge">
                                <div className="selected-user-icon">
                                    <FaUser size={24} />
                                </div>
                                <div className="selected-user-info">
                                    <div className="selected-user-name">{selectedUser.name}</div>
                                    <div className="selected-user-details">
                                        <div className="selected-user-detail-item">
                                            📧 {selectedUser.email}
                                        </div>
                                        <div className="selected-user-detail-item">
                                            📱 {selectedUser.phone}
                                        </div>
                                        <div className="selected-user-detail-item">
                                            🎖️ {selectedUser.currentLevel || 'No Level'}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedUser(null)}
                                    className="remove-user-btn"
                                    title="Remove selected user"
                                >
                                    <FaTimes size={16} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="user-search-container">
                            <input
                                type="text"
                                className="user-search-input"
                                placeholder="Search by name, email, phone, or level..."
                                value={userSearchQuery}
                                onChange={(e) => {
                                    setUserSearchQuery(e.target.value);
                                    setShowUserDropdown(true);
                                }}
                                onFocus={() => setShowUserDropdown(true)}
                            />
                            <FaSearch style={{
                                position: 'absolute',
                                right: '15px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#999'
                            }} />

                            {showUserDropdown && userSearchQuery && (
                                <div className="user-dropdown">
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.slice(0, 10).map(user => (
                                            <div
                                                key={user.uid}
                                                className="user-dropdown-item"
                                                onClick={() => handleUserSelect(user)}
                                            >
                                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                                                    {user.name}
                                                </div>
                                                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                                    {user.email} • {user.phone}
                                                </div>
                                                <div style={{ fontSize: '0.8rem', color: '#4caf50', marginTop: '4px' }}>
                                                    Level: {user.currentLevel || 'Not Set'}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                            No users found
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Commission Toggle Section */}
                <div className="commission-toggle-section">
                    <div className="commission-toggle-header">
                        <h3 style={{ fontSize: '1.1rem', marginBottom: '8px', color: '#333', display: 'flex', alignItems: 'center' }}>
                            💰 Commission Settings
                        </h3>
                        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '12px' }}>
                            {commissionEnabled
                                ? 'Commission will be distributed to the selected user and their upline.'
                                : 'Order will be placed WITHOUT commission distribution.'}
                        </p>
                    </div>

                    <div className="commission-toggle-control">
                        <label className="toggle-label">
                            <span className="toggle-label-text">Commission Distribution:</span>
                            <div className="toggle-switch-wrapper">
                                <input
                                    type="checkbox"
                                    className="toggle-checkbox"
                                    checked={commissionEnabled}
                                    onChange={(e) => setCommissionEnabled(e.target.checked)}
                                    id="commission-toggle"
                                />
                                <label htmlFor="commission-toggle" className="toggle-switch">
                                    <span className="toggle-slider"></span>
                                    <span className={`toggle-status ${commissionEnabled ? 'on' : 'off'}`}>
                                        {commissionEnabled ? 'ON' : 'OFF'}
                                    </span>
                                </label>
                            </div>
                        </label>
                    </div>

                    {!commissionEnabled && (
                        <div className="commission-warning">
                            ⚠️ <strong>Warning:</strong> Commission distribution is disabled. The order will be placed but no commissions will be credited.
                        </div>
                    )}
                </div>

                {/* Product Search */}
                <div style={{ marginBottom: '20px' }}>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px 45px 12px 15px',
                                border: '2px solid #e0e0e0',
                                borderRadius: '8px',
                                fontSize: '16px'
                            }}
                        />
                        <FaSearch style={{
                            position: 'absolute',
                            right: '15px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#999'
                        }} />
                    </div>
                </div>

                {/* Products Grid */}
                <div className="product-grid">
                    {filteredProducts.map(product => {
                        const cartItem = cart.find(item => item.id === product.id);
                        const isInCart = !!cartItem;
                        const cartQuantity = cartItem?.quantity || 0;

                        return (
                            <div
                                key={product.id}
                                className="product-card"
                                style={{
                                    background: isInCart ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' : 'white',
                                    borderRadius: '12px',
                                    padding: '15px',
                                    boxShadow: isInCart ? '0 4px 12px rgba(16, 185, 129, 0.2)' : '0 2px 8px rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s',
                                    border: isInCart ? '2px solid #10b981' : '2px solid transparent',
                                    position: 'relative'
                                }}
                            >
                                {isInCart && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        background: '#10b981',
                                        color: 'white',
                                        padding: '5px 12px',
                                        borderRadius: '20px',
                                        fontSize: '0.85rem',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)'
                                    }}>
                                        <FaShoppingCart size={12} />
                                        {cartQuantity}
                                    </div>
                                )}
                                <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', paddingRight: isInCart ? '70px' : '0' }}>
                                    {product.name}
                                </h3>
                                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '10px' }}>
                                    {product.category}
                                </p>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#10b981' }}>
                                            ₹{product.price || product.mrp}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: product.stock > 0 ? '#059669' : '#f44336', fontWeight: '600' }}>
                                            Stock: {product.stock}
                                        </div>
                                    </div>

                                    {isInCart ? (
                                        // Show quantity controls when in cart
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            background: '#f0fdf4',
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            border: '2px solid #10b981'
                                        }}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateQuantity(product.id, cartQuantity - 1);
                                                }}
                                                style={{
                                                    background: '#ef4444',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    fontSize: '1.2rem',
                                                    transition: 'all 0.3s'
                                                }}
                                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                            >
                                                <FaMinus size={12} />
                                            </button>

                                            <span style={{
                                                fontSize: '1.1rem',
                                                fontWeight: 'bold',
                                                color: '#059669',
                                                minWidth: '30px',
                                                textAlign: 'center'
                                            }}>
                                                {cartQuantity}
                                            </span>

                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const maxStock = Number(product?.stock) || 0;
                                                    if (cartQuantity >= maxStock) {
                                                        showNotification(
                                                            `Only ${maxStock} units available in stock`,
                                                            '⚠️ Stock Limit Reached',
                                                            'warning'
                                                        );
                                                        return;
                                                    }
                                                    updateQuantity(product.id, cartQuantity + 1);
                                                }}
                                                style={{
                                                    background: '#10b981',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    fontSize: '1.2rem',
                                                    transition: 'all 0.3s'
                                                }}
                                                onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                                                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                            >
                                                <FaPlus size={12} />
                                            </button>
                                        </div>
                                    ) : (
                                        // Show "Add" button when not in cart
                                        <button
                                            onClick={() => addToCart(product)}
                                            disabled={!selectedUser || product.stock <= 0}
                                            style={{
                                                background: selectedUser && product.stock > 0 ? '#10b981' : '#ccc',
                                                color: 'white',
                                                border: 'none',
                                                padding: '10px 20px',
                                                borderRadius: '8px',
                                                cursor: selectedUser && product.stock > 0 ? 'pointer' : 'not-allowed',
                                                fontWeight: '600',
                                                transition: 'all 0.3s',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (selectedUser && product.stock > 0) {
                                                    e.target.style.transform = 'scale(1.05)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = 'scale(1)';
                                            }}
                                        >
                                            <FaPlus />
                                            Add
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Floating Cart Button */}
                {cart.length > 0 && (
                    <button
                        className="cart-floating-btn"
                        onClick={() => setShowCartModal(true)}
                    >
                        <FaShoppingCart size={24} />
                        <span className="cart-badge">{getCartItemCount()}</span>
                    </button>
                )}

                {/* Cart Modal */}
                {showCartModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '12px',
                            width: '90%',
                            maxWidth: '800px',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            padding: '30px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                <h2>Cart & Customer Details</h2>
                                <button
                                    onClick={() => setShowCartModal(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '1.5rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            {/* Cart Items */}
                            <div style={{ marginBottom: '30px' }}>
                                <h3>Cart Items</h3>
                                {cart.map(item => (
                                    <div key={item.id} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '15px',
                                        background: '#f5f5f5',
                                        borderRadius: '8px',
                                        marginBottom: '10px'
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: '600' }}>{item.name}</div>
                                            <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                                ₹{item.price || item.mrp} × {item.quantity}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <button
                                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                style={{
                                                    background: '#ff9800',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    width: '30px',
                                                    height: '30px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <FaMinus />
                                            </button>
                                            <span style={{ fontWeight: 'bold', minWidth: '30px', textAlign: 'center' }}>
                                                {item.quantity}
                                            </span>
                                            <button
                                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                style={{
                                                    background: '#4caf50',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    width: '30px',
                                                    height: '30px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <FaPlus />
                                            </button>
                                            <button
                                                onClick={() => removeFromCart(item.id)}
                                                style={{
                                                    background: '#f44336',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    width: '30px',
                                                    height: '30px',
                                                    cursor: 'pointer',
                                                    marginLeft: '10px'
                                                }}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <div style={{
                                    textAlign: 'right',
                                    fontSize: '1.3rem',
                                    fontWeight: 'bold',
                                    marginTop: '15px',
                                    padding: '15px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    borderRadius: '8px'
                                }}>
                                    Total: ₹{getCartTotal().toFixed(2)}
                                </div>
                            </div>

                            {/* Customer Form */}
                            <div style={{ marginTop: '30px' }}>
                                <h3 style={{ marginBottom: '20px' }}>Customer Details</h3>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                            Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => {
                                                const error = validateField('name', e.target.value);
                                                setFormData({ ...formData, name: e.target.value });
                                                setValidationErrors({ ...validationErrors, name: error });
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: `2px solid ${validationErrors.name ? '#f44336' : '#e0e0e0'}`,
                                                borderRadius: '8px'
                                            }}
                                        />
                                        {validationErrors.name && (
                                            <div style={{ color: '#f44336', fontSize: '0.8rem', marginTop: '5px' }}>
                                                {validationErrors.name}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                            Email *
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => {
                                                const error = validateField('email', e.target.value);
                                                setFormData({ ...formData, email: e.target.value });
                                                setValidationErrors({ ...validationErrors, email: error });
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: `2px solid ${validationErrors.email ? '#f44336' : '#e0e0e0'}`,
                                                borderRadius: '8px'
                                            }}
                                        />
                                        {validationErrors.email && (
                                            <div style={{ color: '#f44336', fontSize: '0.8rem', marginTop: '5px' }}>
                                                {validationErrors.email}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                            Phone *
                                        </label>
                                        <input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => {
                                                const error = validateField('phone', e.target.value);
                                                setFormData({ ...formData, phone: e.target.value });
                                                setValidationErrors({ ...validationErrors, phone: error });
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: `2px solid ${validationErrors.phone ? '#f44336' : '#e0e0e0'}`,
                                                borderRadius: '8px'
                                            }}
                                        />
                                        {validationErrors.phone && (
                                            <div style={{ color: '#f44336', fontSize: '0.8rem', marginTop: '5px' }}>
                                                {validationErrors.phone}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                            Birth Date (DD/MM/YYYY) *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.birthDate}
                                            onChange={handleBirthDateInput}
                                            placeholder="DD/MM/YYYY"
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: `2px solid ${validationErrors.birthDate ? '#f44336' : '#e0e0e0'}`,
                                                borderRadius: '8px'
                                            }}
                                        />
                                        {validationErrors.birthDate && (
                                            <div style={{ color: '#f44336', fontSize: '0.8rem', marginTop: '5px' }}>
                                                {validationErrors.birthDate}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                            Address *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.address}
                                            onChange={(e) => {
                                                const error = validateField('address', e.target.value);
                                                setFormData({ ...formData, address: e.target.value });
                                                setValidationErrors({ ...validationErrors, address: error });
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: `2px solid ${validationErrors.address ? '#f44336' : '#e0e0e0'}`,
                                                borderRadius: '8px'
                                            }}
                                        />
                                        {validationErrors.address && (
                                            <div style={{ color: '#f44336', fontSize: '0.8rem', marginTop: '5px' }}>
                                                {validationErrors.address}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                            Landmark
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.landmark}
                                            onChange={(e) => setFormData({ ...formData, landmark: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '8px'
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                            City *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.city}
                                            onChange={(e) => {
                                                const error = validateField('city', e.target.value);
                                                setFormData({ ...formData, city: e.target.value });
                                                setValidationErrors({ ...validationErrors, city: error });
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: `2px solid ${validationErrors.city ? '#f44336' : '#e0e0e0'}`,
                                                borderRadius: '8px'
                                            }}
                                        />
                                        {validationErrors.city && (
                                            <div style={{ color: '#f44336', fontSize: '0.8rem', marginTop: '5px' }}>
                                                {validationErrors.city}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                            State *
                                        </label>
                                        <select
                                            value={formData.state}
                                            onChange={(e) => {
                                                const error = validateField('state', e.target.value);
                                                setFormData({ ...formData, state: e.target.value });
                                                setValidationErrors({ ...validationErrors, state: error });
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: `2px solid ${validationErrors.state ? '#f44336' : '#e0e0e0'}`,
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <option value="">Select State</option>
                                            <option value="Maharashtra">Maharashtra</option>
                                            <option value="Gujarat">Gujarat</option>
                                            <option value="Karnataka">Karnataka</option>
                                            <option value="Tamil Nadu">Tamil Nadu</option>
                                            {/* Add more states as needed */}
                                        </select>
                                        {validationErrors.state && (
                                            <div style={{ color: '#f44336', fontSize: '0.8rem', marginTop: '5px' }}>
                                                {validationErrors.state}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                                            Postal Code *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.postalCode}
                                            onChange={(e) => {
                                                const error = validateField('postalCode', e.target.value);
                                                setFormData({ ...formData, postalCode: e.target.value });
                                                setValidationErrors({ ...validationErrors, postalCode: error });
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                border: `2px solid ${validationErrors.postalCode ? '#f44336' : '#e0e0e0'}`,
                                                borderRadius: '8px'
                                            }}
                                        />
                                        {validationErrors.postalCode && (
                                            <div style={{ color: '#f44336', fontSize: '0.8rem', marginTop: '5px' }}>
                                                {validationErrors.postalCode}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <button
                                    onClick={submitOrder}
                                    disabled={loading}
                                    style={{
                                        width: '100%',
                                        marginTop: '30px',
                                        padding: '15px',
                                        background: loading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '1.1rem',
                                        fontWeight: 'bold',
                                        cursor: loading ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {loading ? 'Processing...' : 'Place Order'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading Overlay */}
                {loading && (
                    <div className="loading-overlay-full">
                        <div className="loading-bar-wrapper">
                            <div className="loading-bar-container">
                                <div className="loading-bar"></div>
                                <p className="loading-text">{loadingMessage}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Notification */}
                {notification.show && (
                    <div className={`notification ${notification.type}`}>
                        {notification.title && (
                            <div className="notification-title">
                                {notification.title}
                            </div>
                        )}
                        <div className="notification-message">
                            {notification.message}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPlaceOrder;
